/**
 * BOXX Print Bridge — browser client.
 * ────────────────────────────────────────────────────────────────────────────
 * Talks to the local "BOXX Print Bridge" tray app over a loopback WebSocket
 * (ws://localhost:8767/print). The bridge enumerates the workstation's REAL
 * Windows printers and prints HTML/PDF/raw label commands SILENTLY to a chosen
 * printer — no Windows print dialog, no PDF detour.
 *
 * This module is product-agnostic and has NO build-tool / framework deps, so it
 * is vendored verbatim into every BOXX product's frontend (see PRODUCT_INTEGRATION.md).
 * React glue lives in usePrintBridge.ts / PrinterPicker.tsx.
 *
 * Canonical source: c:/ManageBOXX/_shared_print_bridge/client/printBridge.ts
 * KEEP COPIES IN SYNC. Any protocol change updates the bridge + this file together.
 *
 * The bridge only upgrades connections from BOXX origins (it validates the
 * Origin header), so a random site can't reach it — but from a BOXX page it is
 * always safe to attempt; callers should still pass a `fallback` for the (common)
 * case where the bridge isn't installed on this workstation.
 */

export interface PrinterInfo {
  name: string
  isDefault: boolean
  /** Driver-reported capabilities (for populating template dropdowns). */
  color?: boolean
  duplex?: boolean
  maxCopies?: number
  paperSizes?: string[]
}

/**
 * Standard print settings the agent can execute. Callers (print templates /
 * printer profiles) set only what they need; the rest use the printer default.
 */
export interface PrintGeometry {
  /** Physical page size — for labels. Omit for normal documents (printer default). */
  widthMm?: number
  heightMm?: number
  /** Uniform page margin in mm (default 0). */
  marginMm?: number
  copies?: number
  landscape?: boolean
  /** 0.1–2.0 (1 = 100%). */
  scale?: number
  /** e.g. "1-3,5". */
  pageRanges?: string
  /** 1,2,4,6,9,16. */
  pagesPerSide?: number
  /** true = colour, false = monochrome (printer default if omitted). */
  color?: boolean
  collate?: boolean
  /** 'simplex' | 'long' | 'short'. */
  duplex?: 'simplex' | 'long' | 'short'
  /** Print CSS backgrounds/colours (default true). */
  printBackgrounds?: boolean
  // ── POS post-actions (ESC/POS, best-effort on the receipt printer) ────────
  /** Pulse the cash drawer after printing. */
  openDrawer?: boolean
  /** Drawer connector: 0 = pin 2 (default), 1 = pin 5. */
  drawerPin?: number
  /** Partial paper cut after printing. */
  cut?: boolean
  /** Buzzer after printing. */
  beep?: boolean
}

export interface PrintOutcome {
  ok: boolean
  pages?: number
  error?: string
}

/** Default loopback endpoint. Override per-workstation via the global below
 *  (e.g. a custom port) without editing vendored copies. */
const DEFAULT_URL = 'ws://localhost:8767/print'

function bridgeUrl(): string {
  if (typeof window !== 'undefined' && (window as any).__BOXX_PRINT_BRIDGE_URL__) {
    return String((window as any).__BOXX_PRINT_BRIDGE_URL__)
  }
  return DEFAULT_URL
}

let _seq = 0
const newReqId = () => `r${Date.now().toString(36)}_${(++_seq).toString(36)}`

interface Pending {
  resolve: (msg: any) => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}

/**
 * One lazily-opened, auto-reconnecting connection shared by the whole page.
 * Requests are correlated by `reqId`, which the bridge echoes on every event.
 */
class BridgeConn {
  private ws: WebSocket | null = null
  private opening: Promise<WebSocket> | null = null
  private readonly pending = new Map<string, Pending>()

  private open(): Promise<WebSocket> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return Promise.resolve(this.ws)
    if (this.opening) return this.opening

    this.opening = new Promise<WebSocket>((resolve, reject) => {
      let settled = false
      let ws: WebSocket
      try {
        ws = new WebSocket(bridgeUrl())
      } catch (e) {
        this.opening = null
        reject(e instanceof Error ? e : new Error('bridge connect failed'))
        return
      }
      // Connection refused (bridge not installed) resolves fast, but guard anyway.
      const t = setTimeout(() => {
        if (settled) return
        settled = true
        this.opening = null
        try { ws.close() } catch { /* ignore */ }
        reject(new Error('bridge connect timeout'))
      }, 1500)

      ws.onopen = () => {
        if (settled) return
        settled = true
        clearTimeout(t)
        this.ws = ws
        this.opening = null
        resolve(ws)
      }
      ws.onerror = () => {
        if (settled) { /* runtime error after open: let onclose handle */ return }
        settled = true
        clearTimeout(t)
        this.opening = null
        reject(new Error('bridge unavailable'))
      }
      ws.onclose = () => {
        if (this.ws === ws) this.ws = null
        this.failAll(new Error('bridge connection closed'))
      }
      ws.onmessage = (ev) => this.dispatch(ev.data)
    })
    return this.opening
  }

  private dispatch(raw: any) {
    let msg: any
    try { msg = JSON.parse(typeof raw === 'string' ? raw : '') } catch { return }
    const id = msg && msg.reqId
    if (!id) return
    const p = this.pending.get(id)
    if (!p) return
    this.pending.delete(id)
    clearTimeout(p.timer)
    p.resolve(msg)
  }

  private failAll(err: Error) {
    for (const [, p] of this.pending) { clearTimeout(p.timer); p.reject(err) }
    this.pending.clear()
  }

  /** Send a command and await the bridge event that carries the same reqId. */
  async request(cmd: string, payload: Record<string, unknown>, timeoutMs = 20000): Promise<any> {
    const ws = await this.open()
    const reqId = newReqId()
    return new Promise<any>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(reqId)
        reject(new Error(`bridge ${cmd} timed out`))
      }, timeoutMs)
      this.pending.set(reqId, { resolve, reject, timer })
      try {
        ws.send(JSON.stringify({ cmd, reqId, ...payload }))
      } catch (e) {
        this.pending.delete(reqId)
        clearTimeout(timer)
        reject(e instanceof Error ? e : new Error('send failed'))
      }
    })
  }
}

const conn = new BridgeConn()

// ── Electron transport ───────────────────────────────────────────────────────
// When the app runs inside a BOXX desktop (Electron) shell, the wrapper exposes
// window.boxxPrintBridge and we silent-print through Electron's own
// webContents.print({silent, deviceName}) — no separate tray agent needed. In a
// plain browser this is absent and we use the loopback agent below. Same client
// API works in both modes (see electron/ in the shared framework for the host).
interface ElectronBridge {
  listPrinters?(): Promise<{ printers: PrinterInfo[]; default: string }>
  print(opts: Record<string, unknown>): Promise<{ ok: boolean; pages?: number; error?: string }>
  openDrawer?(opts: Record<string, unknown>): Promise<{ ok: boolean }>
}
function electronBridge(): ElectronBridge | null {
  if (typeof window === 'undefined') return null
  const b = (window as any).boxxPrintBridge
  return b && typeof b.print === 'function' ? b as ElectronBridge : null
}

// ── detection (cached) ───────────────────────────────────────────────────────
let _detect: { at: number; ok: boolean } | null = null
const DETECT_TTL_MS = 15000

/** True if printing can be done directly (Electron shell OR loopback agent).
 *  Cached briefly so UI can poll cheaply. Never throws. */
export async function detectBridge(force = false): Promise<boolean> {
  if (electronBridge()) return true     // desktop wrapper — always available
  if (!force && _detect && Date.now() - _detect.at < DETECT_TTL_MS) return _detect.ok
  let ok = false
  try {
    const r = await conn.request('hello', {}, 1800)
    ok = r?.event === 'ready'
  } catch { ok = false }
  _detect = { at: Date.now(), ok }
  return ok
}

/** True when running inside a BOXX Electron desktop shell (vs a plain browser). */
export function isElectronShell(): boolean {
  return electronBridge() !== null
}

/** Last known detection state without a round-trip (null = not checked yet). */
export function bridgeKnownAvailable(): boolean | null {
  return _detect ? _detect.ok : null
}

// ── printer list ─────────────────────────────────────────────────────────────
export async function listPrinters(): Promise<{ printers: PrinterInfo[]; default: string }> {
  const eb = electronBridge()
  if (eb?.listPrinters) {
    try {
      const r = await eb.listPrinters()
      return { printers: Array.isArray(r?.printers) ? r.printers : [], default: r?.default || '' }
    } catch { /* fall through to agent */ }
  }
  const r = await conn.request('listPrinters', {}, 6000)
  return {
    printers: Array.isArray(r?.printers) ? r.printers : [],
    default: r?.default || '',
  }
}

// ── printing ─────────────────────────────────────────────────────────────────
async function doPrint(payload: Record<string, unknown>): Promise<PrintOutcome> {
  // Electron desktop shell prints natively (silent, named device); else the agent.
  const eb = electronBridge()
  if (eb) {
    try {
      const r = await eb.print(payload)
      return r?.ok ? { ok: true, pages: r.pages } : { ok: false, error: r?.error || 'print failed' }
    } catch (e: any) { return { ok: false, error: e?.message || 'electron print failed' } }
  }
  const r = await conn.request('print', payload, 60000)
  if (r?.event === 'printed' && r?.ok) return { ok: true, pages: r.pages }
  return { ok: false, error: r?.message || 'print failed' }
}

/** Silently print an HTML document to a named printer. */
export function printHtml(html: string, printerName: string, geo: PrintGeometry = {}): Promise<PrintOutcome> {
  return doPrint({ type: 'html', printerName, data: html, ...geo })
}

/** Silently print a PDF (base64) to a named printer. */
export function printPdfBase64(base64: string, printerName: string, geo: PrintGeometry = {}): Promise<PrintOutcome> {
  return doPrint({ type: 'pdf', printerName, data: base64, ...geo })
}

/** Send raw label commands (ZPL/EPL/TSPL, base64) straight to a printer's spooler. */
export function printRawBase64(base64: string, printerName: string, copies = 1): Promise<PrintOutcome> {
  return doPrint({ type: 'raw', printerName, data: base64, copies })
}

/** Pulse the cash drawer without printing (e.g. a "No Sale" button). */
export async function openDrawer(printerName = '', drawerPin = 0): Promise<{ ok: boolean }> {
  const eb = electronBridge()
  if (eb?.openDrawer) {
    try { return await eb.openDrawer({ printerName, drawerPin }) } catch { return { ok: false } }
  }
  try {
    const r = await conn.request('openDrawer', { printerName, drawerPin }, 6000)
    return { ok: !!(r && r.ok) }
  } catch { return { ok: false } }
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer())
  let bin = ''
  for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i])
  return btoa(bin)
}

// ── the universal helper every print chokepoint calls ────────────────────────
export interface PrintOrFallbackOpts extends PrintGeometry {
  /** The chosen Windows printer name. If empty, we go straight to fallback. */
  printerName?: string | null
  /** Today's behaviour when the bridge can't be used (window.print / PDF / dialog). */
  fallback: () => void
  /** Called after a successful silent print (e.g. a toast). */
  onPrinted?: (pages?: number) => void
  /** Called when the bridge errored and we fell back (e.g. a warning toast). */
  onFallback?: (reason: string) => void
}

/**
 * Print `html` silently via the bridge to `printerName`; if the bridge isn't
 * installed, no printer was chosen, or the bridge errors, run `fallback()`
 * (the page's existing print path). This is the one call every product wires in.
 */
export async function printOrFallback(html: string, opts: PrintOrFallbackOpts): Promise<'bridge' | 'fallback'> {
  const { printerName, fallback, onPrinted, onFallback, ...geo } = opts
  if (!printerName) { fallback(); return 'fallback' }
  let available = false
  try { available = await detectBridge() } catch { available = false }
  if (!available) { onFallback?.('bridge not installed'); fallback(); return 'fallback' }
  try {
    const res = await printHtml(html, printerName, geo)
    if (res.ok) { onPrinted?.(res.pages); return 'bridge' }
    onFallback?.(res.error || 'print failed'); fallback(); return 'fallback'
  } catch (e: any) {
    onFallback?.(e?.message || 'bridge error'); fallback(); return 'fallback'
  }
}

/** Where to send users to install the bridge (overridable per deploy). */
export function bridgeInstallUrl(): string {
  if (typeof window !== 'undefined' && (window as any).__BOXX_PRINT_BRIDGE_INSTALL_URL__) {
    return String((window as any).__BOXX_PRINT_BRIDGE_INSTALL_URL__)
  }
  return 'https://saas-boxx.com/downloads/print-bridge/'
}
